import React, { useState, useEffect } from 'react';
import { SchoolClass, ClassYearConfig, StaffMember } from '../types';
import { useAcademicYear } from '../context/AcademicYearContext';
import { getCachedCollection } from '../services/dataCacheService';
import { collection, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { X, Check, UserCheck, Shield, Phone, Sparkles, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  targetClass: SchoolClass | null;
  currentConfig?: ClassYearConfig;
}

export const ClassStaffModal: React.FC<Props> = ({
  isOpen,
  onClose,
  targetClass,
  currentConfig
}) => {
  const { selectedYear, currentYearObj, updateClassConfig } = useAcademicYear();
  const [teacherName, setTeacherName] = useState('');
  const [teacherPhone, setTeacherPhone] = useState('');
  const [assistantName, setAssistantName] = useState('');
  const [assistantPhone, setAssistantPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [staffList, setStaffList] = useState<StaffMember[]>([]);
  const [loadingStaff, setLoadingStaff] = useState(false);

  // Pre-fill state when targetClass or currentConfig changes
  useEffect(() => {
    if (targetClass) {
      setTeacherName(currentConfig?.teacherName || '');
      setTeacherPhone(currentConfig?.teacherPhone || '');
      setAssistantName(currentConfig?.assistantName || '');
      setAssistantPhone(currentConfig?.assistantPhone || '');
      setNotes(currentConfig?.notes || '');
    }
  }, [targetClass, currentConfig]);

  // Load existing educational and assistant staff to allow easy one-click selection
  useEffect(() => {
    if (!isOpen) return;
    setLoadingStaff(true);
    const fetchStaff = async () => {
      try {
        const staff = await getCachedCollection<StaffMember>('staff');
        setStaffList(staff.filter(s => s.active !== false));
      } catch (e) {
        console.warn('Could not load staff members:', e);
      } finally {
        setLoadingStaff(false);
      }
    };
    fetchStaff();
  }, [isOpen]);

  if (!isOpen || !targetClass) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateClassConfig(targetClass.id, targetClass.name, {
        teacherName: teacherName.trim(),
        teacherPhone: teacherPhone.trim(),
        assistantName: assistantName.trim(),
        assistantPhone: assistantPhone.trim(),
        notes: notes.trim()
      });
      onClose();
    } catch (err) {
      console.error('Failed to save class staff config:', err);
      alert('שגיאה בשמירת פרטי מנהלת הגן והצוות');
    } finally {
      setSaving(false);
    }
  };

  const eduStaff = staffList.filter(s => s.type === 'educational');
  const assistStaff = staffList.filter(s => s.type === 'assistant');

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-xs" dir="rtl">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white w-full max-w-lg rounded-3xl shadow-2xl border border-slate-100 overflow-hidden"
        >
          {/* Header */}
          <div className="px-6 py-5 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-white/10 rounded-2xl backdrop-blur-xs">
                <UserCheck size={24} className="text-indigo-100" />
              </div>
              <div>
                <h3 className="text-lg font-black tracking-tight">הגדרת צוות ומנהלת גן</h3>
                <p className="text-xs text-indigo-100 mt-0.5">
                  כיתת <span className="font-bold underline">{targetClass.name}</span> • שנת לימודים: {currentYearObj.name}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={saving}
              className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-xl transition-all"
            >
              <X size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-5 max-h-[80vh] overflow-y-auto">
            {/* Year Note */}
            <div className="p-3.5 bg-indigo-50 border border-indigo-100 rounded-2xl flex items-center gap-3 text-xs text-indigo-900">
              <Sparkles size={18} className="text-indigo-600 shrink-0" />
              <span>
                הגדרת מנהלת הגן והצוות נשמרת באופן נפרד לשנת <strong>{currentYearObj.name}</strong>, כך שההגדרות של השנים הקודמות נשמרות ללא שינוי.
              </span>
            </div>

            {/* Teacher / Kindergarten Manager Section */}
            <div className="space-y-3 bg-slate-50 p-4 rounded-2xl border border-slate-100">
              <div className="flex items-center justify-between">
                <label className="text-sm font-black text-slate-800 flex items-center gap-1.5">
                  <span>מנהלת הגן / גננת מובילה</span>
                  <span className="text-rose-500">*</span>
                </label>
                {eduStaff.length > 0 && (
                  <span className="text-[11px] text-slate-400">או בחר מתוך צוות קיים</span>
                )}
              </div>

              {/* Quick Select from existing staff */}
              {eduStaff.length > 0 && (
                <select
                  onChange={(e) => {
                    const member = eduStaff.find(s => s.fullName === e.target.value);
                    if (member) {
                      setTeacherName(member.fullName);
                      if (member.phone) setTeacherPhone(member.phone);
                    }
                  }}
                  defaultValue=""
                  className="w-full text-xs bg-white px-3 py-2 rounded-xl border border-slate-200 text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="" disabled>-- בחר מתוך הצוות החינוכי הקיים --</option>
                  {eduStaff.map(s => (
                    <option key={s.id} value={s.fullName}>{s.fullName} ({s.role || 'חינוכי'})</option>
                  ))}
                </select>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold text-slate-500 block mb-1">שם מלא</label>
                  <input
                    type="text"
                    required
                    placeholder="שם מנהלת הגן"
                    value={teacherName}
                    onChange={(e) => setTeacherName(e.target.value)}
                    className="w-full px-3.5 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-slate-500 block mb-1">טלפון</label>
                  <input
                    type="tel"
                    placeholder="מספר נייד"
                    value={teacherPhone}
                    onChange={(e) => setTeacherPhone(e.target.value)}
                    className="w-full px-3.5 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                  />
                </div>
              </div>
            </div>

            {/* Assistant Staff Section */}
            <div className="space-y-3 bg-slate-50 p-4 rounded-2xl border border-slate-100">
              <div className="flex items-center justify-between">
                <label className="text-sm font-black text-slate-800">
                  סייעת הגן (אופציונלי)
                </label>
                {assistStaff.length > 0 && (
                  <span className="text-[11px] text-slate-400">או בחר מתוך סייעות</span>
                )}
              </div>

              {assistStaff.length > 0 && (
                <select
                  onChange={(e) => {
                    const member = assistStaff.find(s => s.fullName === e.target.value);
                    if (member) {
                      setAssistantName(member.fullName);
                      if (member.phone) setAssistantPhone(member.phone);
                    }
                  }}
                  defaultValue=""
                  className="w-full text-xs bg-white px-3 py-2 rounded-xl border border-slate-200 text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="" disabled>-- בחר מתוך צוות הסייעות הקיים --</option>
                  {assistStaff.map(s => (
                    <option key={s.id} value={s.fullName}>{s.fullName}</option>
                  ))}
                </select>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold text-slate-500 block mb-1">שם הסייעת</label>
                  <input
                    type="text"
                    placeholder="שם הסייעת"
                    value={assistantName}
                    onChange={(e) => setAssistantName(e.target.value)}
                    className="w-full px-3.5 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-slate-500 block mb-1">טלפון סייעת</label>
                  <input
                    type="tel"
                    placeholder="מספר נייד"
                    value={assistantPhone}
                    onChange={(e) => setAssistantPhone(e.target.value)}
                    className="w-full px-3.5 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="text-xs font-bold text-slate-700 block mb-1">
                הערות לשנה זו
              </label>
              <textarea
                rows={2}
                placeholder="הערות או דגשים מיוחדים לכיתה זו לשנת הלימודים..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full px-3.5 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="px-5 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-sm font-bold hover:bg-slate-50 transition-colors"
              >
                ביטול
              </button>
              <button
                type="submit"
                disabled={saving || !teacherName.trim()}
                className="px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold shadow-md shadow-indigo-200 flex items-center gap-2 transition-all disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    <span>שומר...</span>
                  </>
                ) : (
                  <>
                    <Check size={16} />
                    <span>שמור לשנת {currentYearObj.name}</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
